import {getReportParams, report} from '../../../../middleware/report';
import {BanTypeEnum} from '../Constants/banTypeEnum';
import {BanPaymentDataType} from '../Constants/banPaymentDataTypeEnum';
import {paymentAction} from '../Constants/debtPaymentEnum';
import {isWithinInvoicePeriod, formatAmount} from '../utils/banTypeClassifier';

export const DWHStatus = {
  Ongoing: 'Ongoing',
  Completed: 'Completed',
  Error: 'Error',
};

export const DwhActionType = {
  AccountListPage: 'Account List Page',
  DebtSumPage: 'Debt Sum Page',
  UpdatePaymentPopUp: 'UpdatePaymentPopUp',
  ChoosePaymentMethodPage: 'Choose Payment method Page',
  BitPaymentDetailsPage: 'BIT Payment Details Page',
  CreditCardPaymentDetailsPage: 'Credit Card Payment Details Page',
  PaymentAndUpdateTransaction: 'Payment & UpdatePayment Transaction',
  PaymentDetailsUpdateLink: 'Payment Details Update Link',
  PaymentOnlyTransaction: 'Payment Only Transaction',
  MustPayExistingDebtPopup: 'MustPayExistingDebtPopup',
  AccountsSelectionPage: 'AccountsSelectionPage',
  CreditCardPage: 'CreditCardPage',
  CreditGuardValidation: 'CreditGuardValidation',
  SettingCreditCard: 'SettingCreditCard',
  PaymentMethodPage: 'Payment Method page',
  StandingOrderAccountPage: 'StandingOrderAccountPage',
  StandingOrderFormPage: 'StandingOrderFormPage',
  SettingStandingOrder: 'SettingStandingOrder',
};

export const DWHMessageText = {
  AccountListPageDisplayed: 'Account List Page Displayed',
  NoAccountExists: 'No Account Exists',
  GeneralError: 'General Error',
  DebtClicked: 'Debt Clicked',
  DebtSumPageDisplayed: 'Debt Sum Page Displayed',
  DebtSumPageButtonClicked: 'Debt Sum Page Button Clicked',
  UpdatePaymentPopUpDisplayed: 'UpdatePaymentPopUp Displayed',
  ContinueWithUpdatePaymentClicked: 'Continue with UpdatePayment Clicked',
  ContinueWithoutUpdatePaymentClicked: 'Continue without UpdatePayment Clicked',
  DebtChoosePaymentMethodDisplayed: 'Debt Choose Payment method Displayed',
  BitPaymentMethodButtonClicked: 'BIT Payment method Button Clicked',
  CreditCardPaymentMethodButtonClicked: 'Credit Card Payment method Button Clicked',
  BitPaymentNotCompletedDisplayed: 'BIT Payment Not Completed Displayed',
  BitPaymentNotPossibleDisplayed: 'BIT Payment Not Possible Displayed',
  Success: 'Success',
  DebtPaymentDetailsPageDisplayed: 'Debt Payment Details Page Displayed',
  PaymentDetailsCGOK: 'Payment Details CG OK',
  PaymentDetailsFailedCGError: 'Payment Details Failed CG Error',
  DebtPaymentDetailsButtonClicked: 'Debt Payment Details Button Clicked',
  SuccessPaymentOnlyUpdateNotPossible: 'Success Payment Only (Update Not Possible)',
  SuccessPaymentOnlyUpdateFailed: 'Success Payment Only (Update Failed)',
  PaymentDetailsUpdateLinkDisplayed: 'Payment Details Update Link Displayed',
  PaymentDetailsUpdateLinkClicked: 'Payment Details Update Link Clicked',
  AccountListPageUpdateButtonClicked: 'Account List Page Update Button Clicked',
  MustPayExistingDebtPopupDisplayed: 'MustPayExistingDebtPopupDisplayed',
  AccountsSelectionPageDisplayed: 'Accounts Selection Page Displayed',
  AccountsSelectionPageButtonClicked: 'Accounts Selection Page Button Clicked',
  CreditCardPageDisplayed: 'CreditCardPage Displayed',
  PaymentEntityButtonClicked: 'PaymentEntity Button Clicked',
  CreditGuardIFrameFailed: 'CreditGuardIFrame Failed',
  CreditGuardValidationButtonClicked: 'CreditGuardValidation Button Clicked',
  CreditGuardValidationSucceeded: 'CreditGuardValidation Succeeded',
  CreditGuardValidationFailed: 'CreditGuardValidation Failed',
  CreditCardPageButtonClicked: 'CreditCardPage Button Clicked',
  PaymentMethodPageDisplayed: 'PaymentMethodPage Displayed',
  PaymentMethodPageButtonClicked: 'PaymentMethodPage Button Clicked',
  StandingOrderPageDisplayed: 'Standing Order Page Displayed',
  NonDigitalBankPopupDisplayed: 'NonDigitalBank Popup Displayed',
  StandingOrderAccountPageButtonClicked: 'StandingOrderAccountPage Button Clicked',
  SettingAStandingOrderPageDisplayed: 'Setting A Standing Order Page Displayed',
  StandingOrderFormPageButtonClicked: 'StandingOrderFormPage Button Clicked',
  SigningFormErrorPopupDisplayed: 'SigningFormErrorPopup Displayed',
};

// Returns ban account status label for param6
// Values: יש חוב / יש חוב מעל תקרה / אין א.ת. ללא חוב / יתרה עתידית / אין חוב או יתרה / חשבון נשלח לחיוב
function getBanStatusLabel(banData) {
  switch (banData?.banType) {
    case BanTypeEnum.BAN_WITH_DEBT:
      return 'יש חוב';
    case BanTypeEnum.BAN_WITH_DEBT_ABOVE_MAX:
      return 'יש חוב מעל תקרה';
    case BanTypeEnum.BAN_WITHOUT_PAYMENT_METHOD:
      return 'אין א.ת. ללא חוב';
    case BanTypeEnum.BAN_WITH_FUTURE_INVOICE:
    case BanTypeEnum.BAN_WITH_BALANCE_FOR_CHARGING:
      return 'יתרה עתידית';
    case BanTypeEnum.BAN_NO_DEBT_NO_PREVIOUS_DEBT:
      return 'אין חוב או יתרה';
    case BanTypeEnum.BAN_NO_DEBT_BUT_HAD_DEBT_IN_INVOICE:
      return 'חשבון נשלח לחיוב';
    default:
      return '';
  }
}

// Returns ban account activity label for param7
// Values: חשבון פעיל / חשבון מושהה / חשבון חשמל
function getBanActivityLabel(banData) {
  if (banData?.isEnergy) return 'חשבון חשמל';
  if (banData?.paused) return 'חשבון מושהה';
  return 'חשבון פעיל';
}

// Returns payment method status label for param8
function getPaymentMethodLabel(banData) {
  return banData?.hasPaymentMethodIssue ? 'אמצעי תשלום לא מעודכן' : 'אמצעי תשלום מעודכן';
}

// Returns existing/last payment method type label for update-payment-method param3
function getExistingPaymentMethodTypeLabel(banData) {
  const paymentType = banData?.paymentData?.paymentType;
  if (!banData.hasPaymentMethodIssue) {
    if (paymentType === BanPaymentDataType.CREDIT_CARD) {
      return 'כ.א.';
    } else if (paymentType === BanPaymentDataType.BANK) {
      return 'הוק';
    } else if (paymentType === BanPaymentDataType.PAYPAL) {
      return 'פייפאל';
    }
    return 'מזומן מאושר';
  } else {
    if (paymentType === BanPaymentDataType.CREDIT_CARD) {
      return 'לא מוגדר עם אחרון כ.א.';
    } else if (paymentType === BanPaymentDataType.BANK) {
      return 'לא מוגדר עם אחרון הוק';
    } else if (paymentType === BanPaymentDataType.PAYPAL) {
      return 'לא מוגדר עם אחרון פייפאל';
    }
    return 'לא מוגדר עם אחרון מזומן';
  }
}

// Returns payment flow label for credit card page param9
// Values: UpdatePaymentFlow / NotUpdatePaymentFlow
function getPaymentFlowLabel(paymentActionType) {
  return paymentActionType === paymentAction.PAYMENT_AND_UPDATE ? 'UpdatePaymentFlow' : 'NotUpdatePaymentFlow';
}

// Returns customer status label for list page param5
// Values: קיים חוב / ע.ת. לא מעודכן / סטטוס תקין
function getListCustomerStatusLabel(allBans) {
  if (allBans.some((ban) => ban.hasDebt)) return 'קיים חוב';
  if (allBans.some((ban) => ban.hasPaymentMethodIssue)) return 'ע.ת. לא מעודכן';
  return 'סטטוס תקין';
}

// Mirrors the futurePaymentAmount() logic from SelectAmount.js
// Returns the future billing amount for a ban, or 0 if none applicable
function calculateBanFutureAmount(banData, yDaysCount = 6, yDaysCountEnergy = 6) {
  const yCount = banData?.isEnergy ? yDaysCountEnergy : yDaysCount;
  const arBalance = parseFloat(banData?.arBalance) || 0;
  const totalDueAmount = parseFloat(banData?.totalDueAmount) || 0;

  if (isWithinInvoicePeriod(banData?.prdCvrgEndDate, 0, banData?.billDueDate) && arBalance >= 0 && arBalance !== totalDueAmount) {
    return arBalance;
  }
  if (isWithinInvoicePeriod(banData?.prdCvrgEndDate, yCount, banData?.billDueDate) && arBalance >= 0 && totalDueAmount >= 0) {
    return totalDueAmount;
  }
  return 0;
}

function initReportParams({actionType, status, messageText}) {
  const reportParams = getReportParams();
  reportParams.area = 'SelfCare';
  reportParams.subArea = 'Private Debt & Update Payment';
  reportParams.channel = 'WebsitePrivate';
  reportParams.actionType = actionType;
  reportParams.status = status;
  reportParams.messageText = messageText;
  return reportParams;
}

function applyLoginData(reportParams, loginData) {
  const firstName = loginData?.userDetails?.firstName ?? '';
  const lastName = loginData?.userDetails?.lastName ?? '';
  reportParams.customerName = `${firstName} ${lastName}`.trim();
  reportParams.email = loginData?.userDetails?.email ?? '';
  reportParams.idNumber = loginData?.userDetails?.IDNumber ?? '';
  reportParams.subscriber = loginData?.userDetails?.contactNumber ?? '';
}

function applyBanParams(reportParams, banData, paymentAmount, yDaysCount, yDaysCountEnergy) {
  const debtAmount = parseFloat(banData?.amount) || 0;
  const futureAmount = calculateBanFutureAmount(banData, yDaysCount, yDaysCountEnergy);
  const totalBalance = debtAmount + futureAmount;
  reportParams.param1 = `סה"כ יתרה: ${totalBalance.toFixed(2)}₪`;
  reportParams.param2 = `חיוב עתידי: ${futureAmount.toFixed(2)}₪`;
  reportParams.param3 = `חוב: ${debtAmount.toFixed(2)}₪`;
  if (paymentAmount != null) {
    reportParams.param4 = `סכום לתשלום ${formatAmount(paymentAmount)}₪`;
  }
  reportParams.param5 = banData?.billDueDate ?? '';
  reportParams.param6 = getBanStatusLabel(banData);
  reportParams.param7 = getBanActivityLabel(banData);
  reportParams.param8 = getPaymentMethodLabel(banData);
  reportParams.banId = banData?.banNumber ?? '';
}

/**
 * Report for Debt List Page events
 * Fired when the ban list screen is entered, has no accounts, or returns an error.
 * param1 = sum of (debt + future payment) across ALL bans.
 * @param {Object} groupedBans - { FINANCIAL_ISSUES: [], NO_FINANCIAL_ISSUES: [] }
 * @param {Object} loginData - state.authentication.loginData
 * @param {string} actionType - DwhActionType value
 * @param {string} messageText - DWHMessageText value
 * @param {string} status - DWHStatus value
 * @param {number} yDaysCount - paymentStatusDisplayDaysY from state
 * @param {number} yDaysCountEnergy - energyPaymentStatusDisplayDaysY from state
 */
export function dwhDebtListPageReport({groupedBans, loginData, actionType, messageText, status, yDaysCount = 6, yDaysCountEnergy = 6}) {
  const allBans = [...(groupedBans?.FINANCIAL_ISSUES ?? []), ...(groupedBans?.NO_FINANCIAL_ISSUES ?? [])];
  const totalDebt = allBans.reduce((sum, ban) => sum + (ban.hasDebt ? parseFloat(ban.amount) || 0 : 0), 0);
  const totalFuture = allBans.reduce((sum, ban) => sum + calculateBanFutureAmount(ban, yDaysCount, yDaysCountEnergy), 0);
  const totalBalance = totalDebt + totalFuture;
  const payableCount = allBans.filter(
    (ban) =>
      ban.banType !== BanTypeEnum.BAN_WITH_DEBT_ABOVE_MAX &&
      (ban.hasDebt || calculateBanFutureAmount(ban, yDaysCount, yDaysCountEnergy) > 0),
  ).length;

  const reportParams = initReportParams({actionType, status, messageText});
  reportParams.param1 = `סה"כ יתרה של כל החשבונות ${totalBalance.toFixed(2)}`;
  reportParams.param2 = `סה"כ חיוב עתידי: ${totalFuture.toFixed(2)}`;
  reportParams.param3 = `סה"כ חוב: ${totalDebt.toFixed(2)}`;
  reportParams.param4 = `חשבונות שניתן לשלם ${payableCount}`;
  reportParams.param5 = getListCustomerStatusLabel(allBans);
  reportParams.banId = allBans.map((ban) => ban.banNumber).join(',');
  applyLoginData(reportParams, loginData);
  report(reportParams);
}

/**
 * Report for ban-level page events.
 * Fired when a specific ban is selected and through BIT payment flow.
 * param1 = ban debt + ban future payment (via isWithinInvoicePeriod logic).
 * @param {Object} banData - selectedBanData from state
 * @param {Object} loginData - state.authentication.loginData
 * @param {number|null} paymentAmount - selected payment amount; omit for "Debt Clicked" (row 5)
 * @param {string} actionType - DwhActionType value
 * @param {string} messageText - DWHMessageText value
 * @param {string} status - DWHStatus value
 * @param {string|null} woCode - work order code; only for BIT General Error (row 14), maps to param9
 * @param {number} yDaysCount - paymentStatusDisplayDaysY from state
 * @param {number} yDaysCountEnergy - energyPaymentStatusDisplayDaysY from state
 */
export function dwhBanReport({
  banData,
  loginData,
  paymentAmount,
  actionType,
  messageText,
  status,
  woCode,
  yDaysCount = 6,
  yDaysCountEnergy = 6,
}) {
  const reportParams = initReportParams({actionType, status, messageText});
  applyBanParams(reportParams, banData, paymentAmount, yDaysCount, yDaysCountEnergy);
  if (woCode != null) {
    reportParams.param9 = woCode;
  }
  applyLoginData(reportParams, loginData);
  report(reportParams);
}

/**
 * Report for Credit Card payment page events.
 * Fired during CG iframe flow, payment button click, transaction results, and update-link events.
 * param1 = ban debt + ban future payment (via isWithinInvoicePeriod logic).
 * @param {Object} banData - selectedBanData from state
 * @param {Object} loginData - state.authentication.loginData
 * @param {number} paymentAmount - selected payment amount
 * @param {string} paymentActionType - paymentAction enum value (PAYMENT or PAYMENT_AND_UPDATE)
 * @param {Array<string>} bansForUpdate - ban numbers selected for CC update (param10 checkbox list)
 * @param {string} actionType - DwhActionType value
 * @param {string} messageText - DWHMessageText value
 * @param {string} status - DWHStatus value
 * @param {number} yDaysCount - paymentStatusDisplayDaysY from state
 * @param {number} yDaysCountEnergy - energyPaymentStatusDisplayDaysY from state
 */
export function dwhCreditCardPageReport({
  banData,
  loginData,
  paymentAmount,
  paymentActionType,
  bansForUpdate,
  actionType,
  messageText,
  status,
  yDaysCount = 6,
  yDaysCountEnergy = 6,
}) {
  const reportParams = initReportParams({actionType, status, messageText});
  applyBanParams(reportParams, banData, paymentAmount, yDaysCount, yDaysCountEnergy);
  reportParams.param9 = getPaymentFlowLabel(paymentActionType);
  if (bansForUpdate?.length > 0) {
    reportParams.param10 = bansForUpdate.join(',');
  }
  applyLoginData(reportParams, loginData);
  report(reportParams);
}

/**
 * Report for Update Payment Method flow events (paymentAction === UPDATE_PAYMENT_METHOD).
 * param1 = selected ban number(s); param3 = existing/last payment method type label.
 */
export function dwhUpdatePaymentMethodReport({
  banData,
  loginData,
  allBans,
  selectedBanNumbers,
  actionType,
  messageText,
  status,
  failureReason,
  param4Value,
  param5Value,
}) {
  const selectedBans = selectedBanNumbers?.length ? selectedBanNumbers : banData?.banNumber ? [banData.banNumber] : [];
  const reportParams = initReportParams({actionType, status, messageText});
  reportParams.param1 = selectedBans.join(', ');
  if (failureReason != null && failureReason !== '') {
    reportParams.param2 = failureReason;
  }
  reportParams.param3 = getExistingPaymentMethodTypeLabel(banData);
  if (param4Value != null && param4Value !== '') {
    reportParams.param4 = param4Value;
  }
  if (param5Value != null && param5Value !== '') {
    reportParams.param5 = param5Value;
  }
  const allBanNumbers = (allBans ?? []).map((ban) => ban.banNumber).filter(Boolean);
  reportParams.banId = allBanNumbers.length > 0 ? allBanNumbers.join(',') : selectedBans.join(',');
  applyLoginData(reportParams, loginData);
  report(reportParams);
}
